import { Alert, Button, Descriptions, Drawer, Empty, Skeleton, Space, Tag, Typography } from 'antd';
import { ExternalLink } from 'lucide-react';
import type {
  IamUserGroupRecord,
  IamUserDetailGroupRecord,
  IamUserPolicyGrantRecord,
} from '@/api/iam.api';
import { fmtTime } from '@/utils/utils';
import './GroupPreviewDrawer.css';

interface GroupPreviewDrawerProps {
  open: boolean;
  group?: IamUserGroupRecord;
  fallbackGroup?: IamUserDetailGroupRecord;
  policies: IamUserPolicyGrantRecord[];
  loading?: boolean;
  error?: unknown;
  onClose: () => void;
  afterClose: () => void;
  onOpenDetail?: (groupId: string) => void;
}

function policyRuleCount(policy: IamUserPolicyGrantRecord): number {
  const ruleCount = policy.actions.reduce(
    (total, action) => total + (action.rules?.length ?? 0),
    0
  );
  return ruleCount || policy.action_total;
}

export default function GroupPreviewDrawer({
  open,
  group,
  fallbackGroup,
  policies,
  loading = false,
  error,
  onClose,
  afterClose,
  onOpenDetail,
}: GroupPreviewDrawerProps) {
  const displayGroup = group ?? fallbackGroup;
  const members = group?.edges.users ?? [];
  const relatedPolicies = [
    ...new Map(policies.map((policy) => [policy.policy_id, policy])).values(),
  ];
  const displayName = displayGroup?.display_name || displayGroup?.name;

  return (
    <Drawer
      className="iam-group-preview-drawer"
      open={open}
      width={680}
      title={
        displayGroup ? (
          <Space size={8} wrap>
            <Typography.Text strong>{displayName}</Typography.Text>
            <Tag bordered={false}>用户组</Tag>
          </Space>
        ) : (
          '用户组详情'
        )
      }
      onClose={onClose}
      afterOpenChange={(nextOpen) => {
        if (!nextOpen) afterClose();
      }}
      footer={
        <div className="iam-group-preview-footer">
          <Button onClick={onClose}>关闭</Button>
          {displayGroup && onOpenDetail && (
            <Button
              type="primary"
              icon={<ExternalLink size={14} />}
              onClick={() => onOpenDetail(displayGroup.id)}
            >
              查看用户组详情
            </Button>
          )}
        </div>
      }
      destroyOnHidden
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          message="用户组详情加载失败"
          description={error instanceof Error ? error.message : String(error)}
        />
      ) : loading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : displayGroup ? (
        <Space direction="vertical" size={20} className="iam-group-preview-body">
          <Descriptions
            column={1}
            bordered
            size="small"
            items={[
              { key: 'name', label: '用户组名称', children: displayName },
              { key: 'id', label: '用户组 ID', children: displayGroup.id },
              {
                key: 'description',
                label: '描述',
                children: displayGroup.comment || '暂无描述',
              },
              {
                key: 'createdAt',
                label: '创建时间',
                children: fmtTime(displayGroup.created_at),
              },
            ]}
          />

          <section className="iam-group-preview-section">
            <Space size={8}>
              <Typography.Title level={5}>用户组成员</Typography.Title>
              <Typography.Text type="secondary">{members.length} 人</Typography.Text>
            </Space>
            {members.length ? (
              <div className="iam-group-preview-list">
                {members.map((member) => (
                  <div className="iam-group-preview-item" key={member.id}>
                    <div className="iam-group-preview-item-main">
                      <Typography.Text strong>{member.name}</Typography.Text>
                      <Typography.Text type="secondary">{member.id}</Typography.Text>
                    </div>
                    <Typography.Text type="secondary" className="iam-group-preview-item-time">
                      创建于 {fmtTime(member.created_at)}
                    </Typography.Text>
                  </div>
                ))}
              </div>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无成员" />
            )}
          </section>

          <section className="iam-group-preview-section">
            <Space size={8}>
              <Typography.Title level={5}>关联权限策略</Typography.Title>
              <Typography.Text type="secondary">{relatedPolicies.length} 条</Typography.Text>
            </Space>
            {relatedPolicies.length ? (
              <div className="iam-group-preview-list">
                {relatedPolicies.map((policy) => (
                  <div className="iam-group-preview-item" key={policy.policy_id}>
                    <div className="iam-group-preview-item-main">
                      <Space size={6} wrap>
                        <Typography.Text strong>
                          {policy.policy_name || policy.policy_id}
                        </Typography.Text>
                        <Tag
                          bordered={false}
                          color={policy.policy_type === 'system' ? 'blue' : 'purple'}
                        >
                          {policy.policy_type === 'system' ? '系统策略' : '自定义策略'}
                        </Tag>
                      </Space>
                      <Typography.Text
                        type="secondary"
                        className="iam-group-preview-item-description"
                      >
                        {policy.policy_description || '暂无描述'}
                      </Typography.Text>
                    </div>
                    <Typography.Text type="secondary" className="iam-group-preview-rule-count">
                      {policyRuleCount(policy)} 条规则
                    </Typography.Text>
                  </div>
                ))}
              </div>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无关联策略" />
            )}
          </section>
        </Space>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无用户组详情" />
      )}
    </Drawer>
  );
}
